import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CuentaMesPoint } from '../../lib/api.js';
import { AXIS, GRID, OTRAS_COLOR, OTRAS_LABEL, SURFACE, TOOLTIP_STYLE } from './ChartCard.js';

const MES_CORTO = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

interface Props {
  meses: CuentaMesPoint[];
  /** Cuentas con color propio, en el orden del ranking del período. */
  series: string[];
  colores: Map<string, string>;
  /** true si hay cuentas plegadas en «Otras». */
  hayOtras: boolean;
}

/**
 * Noches por mes, apiladas por cuenta.
 *
 * Solo entran los meses CON DATO: un mes sin cargar dibujado en cero se leería
 * como «ninguna cuenta produjo», que es justo lo contrario de «no sabemos».
 */
export function CuentasMensualChart({ meses, series, colores, hayOtras }: Props) {
  const data = meses
    .filter((m) => m.conDato)
    .map((m) => {
      const fila: Record<string, string | number> = { label: MES_CORTO[m.mes - 1]! };
      let otras = 0;
      for (const [cuenta, noches] of Object.entries(m.porCuenta)) {
        if (colores.has(cuenta)) fila[cuenta] = noches;
        else otras += noches;
      }
      if (hayOtras) fila[OTRAS_LABEL] = otras;
      return fila;
    });

  const claves = hayOtras ? [...series, OTRAS_LABEL] : series;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="label" tick={AXIS} />
        <YAxis tick={AXIS} allowDecimals={false} />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(v: number) => (v == null ? '—' : v.toLocaleString('es-VE'))}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {claves.map((cuenta, i) => (
          <Bar
            key={cuenta}
            dataKey={cuenta}
            name={cuenta}
            stackId="cuentas"
            fill={colores.get(cuenta) ?? OTRAS_COLOR}
            // 2 px del color de la superficie entre segmentos: los separa sin
            // meter una línea de contorno que compita con el dato.
            stroke={SURFACE}
            strokeWidth={2}
            radius={i === claves.length - 1 ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
